import { resolveStyleDirective, VISUAL_STYLE_PRESETS } from "./visual-styles";

/**
 * 风格 id → 展示名。
 *
 * 🔴 2026-10-11：菜单换成 21 条题材化风格表（见 visual-styles.ts），
 * 旧的 9 条档位**保留可解析**（存量项目的 art_style 不动），但不再出现在选择菜单。
 */
export const ART_STYLE_LABELS: Record<string, string> = {
  ...Object.fromEntries(VISUAL_STYLE_PRESETS.map((p) => [p.id, p.name])),
  // ── 旧档位（仅解析用，菜单不展示）──
  anime: "日系动漫",
  realistic: "真人写实",
  "american-comic": "美式漫画",
  "chinese-ink": "水墨国风",
  cyberpunk: "赛博朋克",
  "flat-illustration": "扁平插画",
  "3d-render": "3D 渲染",
  "oil-painting": "油画风格",
};

export function resolveArtStyleLabel(value?: string | null): string {
  if (!value) return "";
  return ART_STYLE_LABELS[value] ?? value;
}

/**
 * 风格 id → **提示词注入块**（`名称；中文片段`）。
 * 展示用 resolveArtStyleLabel，注入用本函数——两者不可混用：
 * 摘要/卡片上加长片段会糊成一片，提示词里少了片段则风格不生效。
 */
export function resolveArtStyleDirectiveForPrompt(value?: string | null): string {
  if (!value) return "";
  return resolveStyleDirective(value) || resolveArtStyleLabel(value);
}

export function normalizeAspectRatio(value?: string | null): "16:9" | "9:16" | "1:1" | "4:3" | "3:4" {
  return value === "9:16" || value === "1:1" || value === "4:3" || value === "3:4" ? value : "16:9";
}

/**
 * 视觉风格「六件套」在剧本里的呈现块（§1 视觉风格）。
 *
 * 🔴 2026-09-18（用户：视觉风格要单独栏目推理、可锁定、不跟角色和剧情走）：
 * 这一段不再由主生成流程顺手写，而是由项目「视觉风格设定」**单独供应**：
 *   - 生成剧本时后端把它拼在模型输出之前（模型被要求不写 §1）
 *   - 剧本里的 §1 可随时用设定卡一键覆写（replaceStyleSection）
 * 因此格式必须与 prompt registry 里 script_generate 的 §1 规格逐字一致（标签 + 中文冒号 + 单行）。
 */
const RATIO_FULL: Record<string, string> = {
  "16:9": "16:9 横屏",
  "9:16": "9:16 竖屏",
  "2.35:1": "2.35:1 宽银幕",
  "1:1": "1:1 方形",
  "4:3": "4:3 横屏",
  "3:4": "3:4 竖屏",
};

export interface StyleBundleLike {
  artStyle?: string | null;
  colorPalette?: string | null;
  eraAesthetics?: string | null;
  mood?: string | null;
  aspectRatio?: string | null;
  referenceDirector?: string | null;
}

/** 除画幅比例外是否有任何显式设定（画幅恒有默认值，不能作为「用户设过」的判据） */
export function hasAnyStyleSetting(s?: StyleBundleLike): boolean {
  return !!(
    String(s?.artStyle ?? "").trim() ||
    String(s?.colorPalette ?? "").trim() ||
    String(s?.eraAesthetics ?? "").trim() ||
    String(s?.mood ?? "").trim() ||
    String(s?.referenceDirector ?? "").trim()
  );
}

/**
 * 折叠态摘要。
 * 🔴 注意：画幅比例**恒有默认值**（16:9），所以「一个字都没设」时必须返回空串 ——
 * 否则调用方用 `!!styleSummary(...)` 判断「用户设过风格」会永远为真，
 * UI 会把「模型自行推断」的剧本错标成「已按你的设定生成」。
 */
export function styleSummary(s?: StyleBundleLike): string {
  const style = resolveArtStyleLabel(s?.artStyle);
  const tone = String(s?.colorPalette ?? "").trim();
  const era = String(s?.eraAesthetics ?? "").trim();
  const mood = String(s?.mood ?? "").trim();
  const director = String(s?.referenceDirector ?? "").trim();
  if (!style && !tone && !era && !mood && !director) return "";
  return [style, tone, era, mood, normalizeAspectRatio(s?.aspectRatio), director]
    .filter(Boolean)
    .join(" · ");
}

/** 渲染 §1 视觉风格块（缺值字段写「无」，保证六个机器可读字段齐全） */
export function renderStyleBlock(s?: StyleBundleLike): string {
  const ratio = normalizeAspectRatio(s?.aspectRatio);
  return [
    "=== 1. 视觉风格 ===",
    "",
    `视觉风格：${resolveArtStyleLabel(s?.artStyle) || "无"}`,
    `色彩基调：${String(s?.colorPalette ?? "").trim() || "无"}`,
    `时代美学：${String(s?.eraAesthetics ?? "").trim() || "无"}`,
    `氛围情绪：${String(s?.mood ?? "").trim() || "无"}`,
    `画幅比例：${RATIO_FULL[ratio] ?? ratio}`,
    `参考导演：${String(s?.referenceDirector ?? "").trim() || "无"}`,
  ].join("\n");
}

/**
 * 章节头识别。规范格式是 `=== N. 标题 ===`，但模型偶尔会自作主张改用
 * markdown 标题（`## 2. 角色描述`）或裸编号（`2. 角色描述`）。
 * 剥 §1 时若认不出下一个章节头，会把 §2/§3 整段当成 §1 的尾巴一起删掉 ——
 * 所以这里三种写法都要认。裸编号只在后面跟着已知章节名时才认，避免误伤正文里
 * 以「1.」开头的普通句子。
 */
const SECTION_HEAD_RE =
  /^[ \t]*(?:={2,}|#{1,6})[ \t]*\d+[ \t]*[.、][ \t]*\S|^[ \t]*\d+[ \t]*[.、][ \t]*(?:视觉风格|角色描述|场景)/m;
/** 「§1 视觉风格」章节头 */
const STYLE_HEAD_RE =
  /^[ \t]*(?:={2,}|#{1,6})?[ \t]*1[ \t]*[.、][ \t]*视觉风格[ \t]*(?:={2,})?[ \t]*$/m;

/** 定位 §1 视觉风格段在原文里的 [start, end) 区间（end 为下一个章节头起始，无则到文末） */
function locateStyleSection(src: string): { start: number; end: number } | null {
  const m = STYLE_HEAD_RE.exec(src);
  if (!m) return null;
  const start = m.index;
  const rest = src.slice(start + m[0].length);
  const next = SECTION_HEAD_RE.exec(rest);
  const end = next ? start + m[0].length + next.index : src.length;
  return { start, end };
}

const trimEdges = (s: string) => s.replace(/^\s+|\s+$/g, "");

/**
 * 剥掉 §1 视觉风格段（模型若仍输出，拼接系统块前先去掉，避免重复）。
 * 只动 §1 那一刀，**不对正文做任何空白压缩** —— 剧本里的空行是排版信息。
 */
export function stripStyleSection(text: string): string {
  const src = String(text ?? "");
  const loc = locateStyleSection(src);
  if (!loc) return src;
  return [trimEdges(src.slice(0, loc.start)), trimEdges(src.slice(loc.end))]
    .filter(Boolean)
    .join("\n\n");
}

/**
 * 用系统块整体替换 §1（没有 §1 就前置补上）——「同步到剧本」按钮的实现。
 * 🔴 必须是外科手术式替换：§2 角色描述 / §3 场景 逐字保留，
 * 否则一次「同步风格」就把整部剧本冲掉了。
 */
export function replaceStyleSection(text: string, block: string): string {
  const src = String(text ?? "");
  if (!src.trim()) return block;
  const loc = locateStyleSection(src);
  if (!loc) return `${block}\n\n${trimEdges(src)}`;
  return [trimEdges(src.slice(0, loc.start)), block, trimEdges(src.slice(loc.end))]
    .filter(Boolean)
    .join("\n\n");
}

/** 从任意文本（模型回复 / 剧本片段）里抽出六个风格字段 */
export function parseStyleBlock(text: string): StyleBundleLike {
  const src = String(text ?? "");
  const pick = (label: string): string => {
    const m = new RegExp(`^[ \\t]*${label}[ \\t]*[:：][ \\t]*(.+)$`, "m").exec(src);
    return m ? m[1].trim() : "";
  };
  const rawRatio = pick("画幅比例");
  const ratioMatch = /(\d+(?:\.\d+)?)\s*[:：]\s*(\d+(?:\.\d+)?)/.exec(rawRatio);
  const ratio = ratioMatch ? `${ratioMatch[1]}:${ratioMatch[2]}` : rawRatio;
  const clean = (v: string) => (v === "无" || v === "（未指定）" ? "" : v);
  return {
    artStyle: clean(pick("视觉风格")),
    colorPalette: clean(pick("色彩基调")),
    eraAesthetics: clean(pick("时代美学")),
    mood: clean(pick("氛围情绪")),
    aspectRatio: ratio || "16:9",
    referenceDirector: clean(pick("参考导演")),
  };
}

/**
 * 剧本生成时给模型的「§1 由系统供应」指令。
 * 关键：让模型**不要输出 §1**，只写 §2 角色描述 / §3 场景，
 * 这样视觉风格就不跟角色与剧情在同一轮推理里被顺手改写。
 */
export function buildStyleLockDirective(s: StyleBundleLike, locked: boolean): string {
  return [
    "【§1 视觉风格：由系统统一附上，你不要输出这一章】",
    "本片的视觉风格已由项目「视觉风格设定」单独确定，系统会在你的输出前面自动拼上 §1 视觉风格章节。",
    "因此你**只输出 §2 角色描述 与 §3 场景 两个章节**，绝对不要输出「=== 1. 视觉风格 ===」这一章，也不要输出它的任何字段行。",
    locked
      ? "该设定处于**锁定**状态：无论你认为剧情更适合别的风格，都一律不得改写，也不得在角色/场景里另立风格。"
      : "该设定当前未锁定，但本次生成仍以它为准，不要在正文里另立风格。",
    "下面这些值你可以参考，以保持角色与场景的视觉语言一致（但不要复述为 §1 章节）：",
    `- 视觉风格：${resolveArtStyleLabel(s?.artStyle) || "（未指定）"}`,
    `- 色彩基调：${String(s?.colorPalette ?? "").trim() || "（未指定）"}`,
    `- 时代美学：${String(s?.eraAesthetics ?? "").trim() || "（未指定）"}`,
    `- 氛围情绪：${String(s?.mood ?? "").trim() || "（未指定）"}`,
    `- 画幅比例：${RATIO_FULL[normalizeAspectRatio(s?.aspectRatio)] ?? normalizeAspectRatio(s?.aspectRatio)}`,
    `- 参考导演：${String(s?.referenceDirector ?? "").trim() || "（未指定）"}`,
  ].join("\n");
}

export function buildVisualDirective(settings?: {
  artStyle?: string | null;
  aspectRatio?: string | null;
  colorPalette?: string | null;
  eraAesthetics?: string | null;
  mood?: string | null;
  referenceDirector?: string | null;
}): string {
  const style = resolveArtStyleLabel(settings?.artStyle);
  const ratio = normalizeAspectRatio(settings?.aspectRatio);
  // 2026-09-18 六件套扩展：色彩基调 / 时代美学 / 氛围情绪 / 参考导演（均可手填，为空不占行）
  const tone = String(settings?.colorPalette ?? "").trim();
  const era = String(settings?.eraAesthetics ?? "").trim();
  const mood = String(settings?.mood ?? "").trim();
  const director = String(settings?.referenceDirector ?? "").trim();
  return [
    "【用户选择的视觉设定｜最高优先级】",
    style ? `- 画面风格：${style}。所有角色、场景、分镜、视频提示词都必须服从此风格，不要自行改成其它风格。` : "",
    `- 画面比例：${ratio}。构图、场景描述、镜头语言和视频生成提示词都必须按此画幅规划。`,
    tone ? `- 色彩基调：${tone}。` : "",
    era ? `- 时代美学：${era}。` : "",
    mood ? `- 氛围情绪：${mood}。` : "",
    director ? `- 参考导演手法：${director}。运镜、构图、光影可借鉴其标志性风格，但内容仍以剧本为准。` : "",
  ].filter(Boolean).join("\n");
}
